import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ShopDocumentsManager } from '../components/ShopDocuments';
import {
  Check, ArrowLeft, ArrowRight, ShieldCheck, Store, FileText,
  FileSignature, Save, Sparkles, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 'info', icon: Store, key: 'info' },
  { id: 'requisites', icon: FileSignature, key: 'requisites' },
  { id: 'documents', icon: FileText, key: 'documents' },
  { id: 'verification', icon: ShieldCheck, key: 'verification' },
];

const STEP_TITLES = {
  info: { lv: 'Veikala informācija', en: 'Shop information' },
  requisites: { lv: 'Rekvizīti', en: 'Requisites' },
  documents: { lv: 'Dokumenti', en: 'Documents' },
  verification: { lv: 'Verifikācija', en: 'Verification' },
};

const ENTITY_TYPES = [
  { value: 'sia', lv: 'SIA (Sabiedrība ar ierobežotu atbildību)', en: 'Ltd (SIA)' },
  { value: 'ik', lv: 'Individuālais komersants (IK)', en: 'Sole proprietor (IK)' },
  { value: 'as', lv: 'AS (Akciju sabiedrība)', en: 'Joint stock company (AS)' },
  { value: 'pasvaldiba', lv: 'Pašvaldība / valsts iestāde', en: 'Municipality / state agency' },
  { value: 'biedriba', lv: 'Biedrība / nodibinājums', en: 'Association / foundation' },
  { value: 'individual', lv: 'Fiziska persona', en: 'Individual' },
];

const AUTOSAVE_DEBOUNCE_MS = 1500;

export default function ShopOnboardingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registryCheck, setRegistryCheck] = useState(null);

  const [formData, setFormData] = useState({
    shop_name: '',
    shop_description: '',
    shop_phone: '',
    entity_type: '',
    legal_name: '',
    registration_number: '',
    vat_number: '',
    default_vat_rate: '21',
    legal_address: '',
    bank_name: '',
    bank_account: '',
  });

  const draftTimeout = useRef(null);
  const lastSavedRef = useRef('');

  // ---------- Initial load ----------
  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/shop-profile/onboarding`);
        setStatus(data);
        // Prefer existing profile values; fall back to draft, fall back to empty.
        const src = data.profile?.shop_name ? data.profile : (data.draft || {});
        setFormData((prev) => ({
          ...prev,
          shop_name: src.shop_name || '',
          shop_description: src.shop_description || '',
          shop_phone: src.shop_phone || '',
          entity_type: src.entity_type || '',
          legal_name: src.legal_name || '',
          registration_number: src.registration_number || '',
          vat_number: src.vat_number || '',
          default_vat_rate: src.default_vat_rate !== undefined && src.default_vat_rate !== null ? String(src.default_vat_rate) : '21',
          legal_address: src.legal_address || '',
          bank_name: src.bank_name || '',
          bank_account: src.bank_account || '',
        }));
        // Resume at the first incomplete step.
        const idx = STEPS.findIndex((s) => s.id === data.next_step);
        setStepIdx(idx === -1 ? 3 : idx);
      } catch (e) {
        console.error(e);
        setError(language === 'lv' ? 'Neizdevās ielādēt onboarding datus' : 'Failed to load onboarding data');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate, language]);

  // Check the official register so we can offer instant verification when the
  // seller's registration number matches an active entry.
  useEffect(() => {
    if (!status?.has_profile || status?.is_verified) return;
    axios.get(`${API}/verification/registry-check`)
      .then(({ data }) => setRegistryCheck(data))
      .catch(() => setRegistryCheck(null));
  }, [status?.has_profile, status?.is_verified, stepIdx]);

  // ---------- Debounced draft autosave ----------
  const scheduleAutosave = useCallback((next) => {
    if (draftTimeout.current) clearTimeout(draftTimeout.current);
    const snapshot = JSON.stringify(next);
    if (snapshot === lastSavedRef.current) return;
    setSaveState('saving');
    draftTimeout.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/shop-profile/draft`, next);
        lastSavedRef.current = snapshot;
        setSaveState('saved');
        setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2000);
      } catch (e) {
        console.error(e);
        setSaveState('error');
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, []);

  const updateField = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      scheduleAutosave(next);
      return next;
    });
  };

  // ---------- Step gating ----------
  const stepValid = (idx) => {
    const step = STEPS[idx];
    if (step.id === 'info') {
      return formData.shop_name.trim() && formData.shop_description.trim() && formData.shop_phone.trim();
    }
    if (step.id === 'requisites') {
      const et = formData.entity_type;
      if (!et) return false;
      if (et === 'individual') return formData.legal_address.trim();
      return formData.legal_name.trim() && formData.registration_number.trim() && formData.legal_address.trim();
    }
    if (step.id === 'documents') return (status?.documents_count || 0) > 0;
    if (step.id === 'verification') return status?.is_verified;
    return false;
  };

  const persistProfile = async () => {
    // Create or update the real shop profile based on whether one exists.
    if (status?.has_profile) {
      await axios.put(`${API}/shop-profile`, formData);
    } else {
      await axios.post(`${API}/shop-profile`, formData);
    }
    // Clear the draft now that data lives in the canonical collection.
    await axios.delete(`${API}/shop-profile/draft`).catch(() => {});
  };

  const goNext = async () => {
    setError('');
    setSubmitting(true);
    try {
      // info + requisites steps require persisting to shop_profiles.
      if (STEPS[stepIdx].id === 'info' || STEPS[stepIdx].id === 'requisites') {
        await persistProfile();
        // Refresh status snapshot so subsequent steps see the saved profile.
        const { data } = await axios.get(`${API}/shop-profile/onboarding`);
        setStatus(data);
      }
      if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
    } catch (e) {
      setError(e.response?.data?.detail || (language === 'lv' ? 'Saglabāšana neizdevās' : 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitVerification = async () => {
    setSubmitting(true); setError('');
    try {
      await axios.post(`${API}/verification/request`, {});
      const { data } = await axios.get(`${API}/shop-profile/onboarding`);
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.detail || (language === 'lv' ? 'Pieprasījums neizdevās' : 'Request failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const refreshDocuments = async () => {
    try {
      const { data } = await axios.get(`${API}/shop-profile/onboarding`);
      setStatus(data);
    } catch (e) { /* swallow */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D97736] animate-spin" />
      </div>
    );
  }

  const currentStep = STEPS[stepIdx];
  const stepsDone = status?.steps_done || 0;
  const progress = Math.round((stepsDone / 4) * 100);

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8" data-testid="shop-onboarding-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/shop-profile')}
          className="mb-4 text-[#5C5C5C]"
          data-testid="back-to-shop-profile"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'lv' ? 'Atpakaļ uz veikala profilu' : 'Back to shop profile'}
        </Button>

        {/* Header */}
        <div className="mb-8 text-center">
          <div className="w-14 h-14 mx-auto mb-3 bg-[#D97736]/10 rounded-2xl flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-[#D97736]" />
          </div>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-[#121212] mb-2">
            {language === 'lv' ? 'Veikala iestatīšana' : 'Shop setup'}
          </h1>
          <p className="text-[#5C5C5C]">
            {language === 'lv'
              ? '4 vienkārši soļi, lai verificētu veikalu un saņemtu bezmaksas kataloga piekļuvi'
              : '4 simple steps to verify your shop and unlock free catalog access'}
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-[#5C5C5C]">
              {language === 'lv' ? 'Progress' : 'Progress'}
            </span>
            <span className="text-sm font-medium text-[#121212]" data-testid="onboarding-progress">
              {stepsDone}/4 ({progress}%)
            </span>
          </div>
          <div className="h-2 bg-[#E5E4E0] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#D97736] to-[#F0A05A] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step indicators */}
        <div className="grid grid-cols-4 gap-2 mb-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isCurrent = i === stepIdx;
            const isDone = i === 0 ? status?.info_complete
              : i === 1 ? status?.requisites_complete
              : i === 2 ? status?.has_documents
              : status?.is_verified;
            const isPast = i < stepIdx;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => isDone || isPast ? setStepIdx(i) : null}
                disabled={!isDone && !isPast && !isCurrent}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  isCurrent ? 'border-[#D97736] bg-[#D97736]/5'
                  : isDone ? 'border-green-300 bg-green-50 cursor-pointer hover:border-green-400'
                  : 'border-[#E5E4E0] bg-white opacity-60'
                }`}
                data-testid={`step-indicator-${s.id}`}
              >
                <div className="flex justify-center mb-1">
                  {isDone ? (
                    <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <Icon className={`w-7 h-7 ${isCurrent ? 'text-[#D97736]' : 'text-[#8A8A8A]'}`} />
                  )}
                </div>
                <div className={`text-xs font-medium ${isCurrent ? 'text-[#121212]' : 'text-[#5C5C5C]'}`}>
                  {STEP_TITLES[s.id][language] || STEP_TITLES[s.id].lv}
                </div>
              </button>
            );
          })}
        </div>

        {/* Step content */}
        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 sm:p-8 mb-6 shadow-sm">
          {currentStep.id === 'info' && (
            <StepInfo formData={formData} updateField={updateField} language={language} />
          )}
          {currentStep.id === 'requisites' && (
            <StepRequisites formData={formData} updateField={updateField} language={language} />
          )}
          {currentStep.id === 'documents' && (
            <StepDocuments
              user={user}
              language={language}
              hasDocs={status?.has_documents}
              onChange={refreshDocuments}
            />
          )}
          {currentStep.id === 'verification' && (
            <StepVerification
              language={language}
              status={status}
              registryCheck={registryCheck}
              onSubmit={submitVerification}
              submitting={submitting}
            />
          )}

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="onboarding-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-[#8A8A8A] flex items-center gap-2" data-testid="autosave-status">
            {saveState === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin" />{language === 'lv' ? 'Saglabā…' : 'Saving…'}</>)}
            {saveState === 'saved' && (<><Check className="w-3 h-3 text-green-600" />{language === 'lv' ? 'Saglabāts' : 'Saved'}</>)}
            {saveState === 'error' && (<span className="text-red-600">{language === 'lv' ? 'Saglabāšana neizdevās' : 'Save failed'}</span>)}
            {saveState === 'idle' && (<><Save className="w-3 h-3" />{language === 'lv' ? 'Auto-saglabāšana ieslēgta' : 'Autosave on'}</>)}
          </div>

          <div className="flex gap-2">
            {stepIdx > 0 && (
              <Button
                variant="outline"
                onClick={() => setStepIdx(stepIdx - 1)}
                disabled={submitting}
                data-testid="onboarding-prev"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {language === 'lv' ? 'Atpakaļ' : 'Back'}
              </Button>
            )}
            {currentStep.id === 'verification' ? (
              status?.is_verified ? (
                <Button
                  onClick={() => navigate('/catalog')}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="onboarding-finish"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {language === 'lv' ? 'Pāriet uz katalogu' : 'Go to catalog'}
                </Button>
              ) : null
            ) : (
              <Button
                onClick={goNext}
                disabled={!stepValid(stepIdx) || submitting}
                className="bg-[#D97736] hover:bg-[#C06528] text-white"
                data-testid="onboarding-next"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                {language === 'lv' ? 'Tālāk' : 'Next'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============= Step components =============

function StepInfo({ formData, updateField, language }) {
  return (
    <div className="space-y-4">
      <h2 className="font-outfit font-semibold text-xl text-[#121212]">
        {language === 'lv' ? 'Pastāsti par savu veikalu' : 'Tell us about your shop'}
      </h2>
      <div>
        <Label>{language === 'lv' ? 'Veikala nosaukums *' : 'Shop name *'}</Label>
        <Input
          value={formData.shop_name}
          onChange={(e) => updateField('shop_name', e.target.value)}
          placeholder={language === 'lv' ? 'piem., Lauku medus' : 'e.g., Country Honey'}
          data-testid="onb-shop-name"
        />
      </div>
      <div>
        <Label>{language === 'lv' ? 'Apraksts *' : 'Description *'}</Label>
        <Textarea
          value={formData.shop_description}
          onChange={(e) => updateField('shop_description', e.target.value)}
          rows={4}
          placeholder={language === 'lv'
            ? 'Pastāsti, ko piedāvā un kas Tevi atšķir no citiem'
            : 'What you sell and what makes you different'}
          data-testid="onb-shop-description"
        />
      </div>
      <div>
        <Label>{language === 'lv' ? 'Veikala telefons *' : 'Shop phone *'}</Label>
        <Input
          value={formData.shop_phone}
          onChange={(e) => updateField('shop_phone', e.target.value)}
          placeholder="+371 ..."
          data-testid="onb-shop-phone"
        />
      </div>
    </div>
  );
}

function StepRequisites({ formData, updateField, language }) {
  const isIndividual = formData.entity_type === 'individual';
  return (
    <div className="space-y-4">
      <h2 className="font-outfit font-semibold text-xl text-[#121212]">
        {language === 'lv' ? 'Juridiskā informācija' : 'Legal information'}
      </h2>
      <p className="text-sm text-[#5C5C5C]">
        {language === 'lv'
          ? 'Šī informācija ir nepieciešama verifikācijai un rēķinu izrakstīšanai.'
          : 'Required for verification and invoicing.'}
      </p>
      <div>
        <Label>{language === 'lv' ? 'Uzņēmuma tips *' : 'Entity type *'}</Label>
        <Select value={formData.entity_type} onValueChange={(v) => updateField('entity_type', v)}>
          <SelectTrigger data-testid="onb-entity-type">
            <SelectValue placeholder={language === 'lv' ? 'Izvēlies...' : 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {ENTITY_TYPES.map((et) => (
              <SelectItem key={et.value} value={et.value} data-testid={`onb-entity-${et.value}`}>
                {et[language] || et.lv}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!isIndividual && formData.entity_type && (
        <>
          <div>
            <Label>{language === 'lv' ? 'Juridiskais nosaukums *' : 'Legal name *'}</Label>
            <Input
              value={formData.legal_name}
              onChange={(e) => updateField('legal_name', e.target.value)}
              placeholder={language === 'lv' ? 'piem., SIA Lauku medus' : 'e.g., Country Honey Ltd'}
              data-testid="onb-legal-name"
            />
          </div>
          <div>
            <Label>{language === 'lv' ? 'Reģistrācijas numurs *' : 'Registration number *'}</Label>
            <Input
              value={formData.registration_number}
              onChange={(e) => updateField('registration_number', e.target.value)}
              placeholder="40003123456"
              data-testid="onb-reg-number"
            />
          </div>
          <div>
            <Label>{language === 'lv' ? 'PVN numurs (ja ir)' : 'VAT number (if any)'}</Label>
            <Input
              value={formData.vat_number}
              onChange={(e) => updateField('vat_number', e.target.value)}
              placeholder="LV40003123456"
              data-testid="onb-vat-number"
            />
          </div>
          {formData.vat_number && (
            <div data-testid="onb-default-vat-rate-field">
              <Label>{language === 'lv' ? 'Noklusējuma PVN likme' : 'Default VAT rate'}</Label>
              <p className="text-xs text-[#5C5C5C] mt-0.5 mb-1">
                {language === 'lv'
                  ? 'Piemēro pārdošanas dokumentos, ja precei nav norādīta cita likme.'
                  : 'Applied on sales documents when a listing has no specific rate.'}
              </p>
              <select
                value={formData.default_vat_rate}
                onChange={(e) => updateField('default_vat_rate', e.target.value)}
                className="h-11 w-full rounded-md bg-white border border-[#E5E4E0] px-3 text-sm focus:border-[#D97736] focus:outline-none"
                data-testid="onb-default-vat-rate"
              >
                <option value="21">21% {language === 'lv' ? '(standarta)' : '(standard)'}</option>
                <option value="12">12% {language === 'lv' ? '(pārtika, medikamenti, grāmatas)' : '(food, meds, books)'}</option>
                <option value="5">5% {language === 'lv' ? '(noteiktas grāmatas)' : '(certain books)'}</option>
                <option value="0">0% {language === 'lv' ? '(eksports)' : '(export)'}</option>
              </select>
            </div>
          )}
        </>
      )}
      <div>
        <Label>{language === 'lv' ? 'Juridiskā adrese *' : 'Legal address *'}</Label>
        <Input
          value={formData.legal_address}
          onChange={(e) => updateField('legal_address', e.target.value)}
          placeholder={language === 'lv' ? 'Iela 1, Rīga, LV-1010' : 'Street 1, Riga, LV-1010'}
          data-testid="onb-legal-address"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{language === 'lv' ? 'Banka' : 'Bank'}</Label>
          <Input
            value={formData.bank_name}
            onChange={(e) => updateField('bank_name', e.target.value)}
            placeholder="Swedbank"
            data-testid="onb-bank-name"
          />
        </div>
        <div>
          <Label>IBAN</Label>
          <Input
            value={formData.bank_account}
            onChange={(e) => updateField('bank_account', e.target.value)}
            placeholder="LV00 HABA 0000 ..."
            data-testid="onb-bank-account"
          />
        </div>
      </div>
    </div>
  );
}

function StepDocuments({ user, language, hasDocs, onChange }) {
  return (
    <div className="space-y-4">
      <h2 className="font-outfit font-semibold text-xl text-[#121212]">
        {language === 'lv' ? 'Apstiprinošie dokumenti' : 'Supporting documents'}
      </h2>
      <p className="text-sm text-[#5C5C5C]">
        {language === 'lv'
          ? 'Augšupielādē vismaz vienu dokumentu (UR izziņa, sertifikāts, licence vai jebkurš cits dokuments, kas apliecina tavu darbību).'
          : 'Upload at least one document (registry extract, certificate, license, or anything proving your activity).'}
      </p>
      {hasDocs && (
        <div className="p-3 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {language === 'lv' ? 'Lieliski! Dokumenti pievienoti.' : 'Great! Documents added.'}
        </div>
      )}
      <ShopDocumentsManager userId={user?.id} onChange={onChange} />
    </div>
  );
}

function StepVerification({ language, status, registryCheck, onSubmit, submitting }) {
  const autoVerify = registryCheck?.can_auto_verify;
  const duplicate = registryCheck?.duplicate;
  return (
    <div className="space-y-4 text-center">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#D97736]/10 flex items-center justify-center">
        <ShieldCheck className="w-8 h-8 text-[#D97736]" />
      </div>
      <h2 className="font-outfit font-semibold text-xl text-[#121212]">
        {language === 'lv' ? 'Lūdz verifikāciju' : 'Request verification'}
      </h2>

      {status?.is_verified ? (
        <div className="p-4 rounded-xl bg-green-50 border-2 border-green-300 text-green-800">
          <Check className="w-8 h-8 mx-auto mb-2" />
          <p className="font-medium">
            {language === 'lv' ? 'Tavs veikals ir verificēts!' : 'Your shop is verified!'}
          </p>
          <p className="text-sm mt-1">
            {language === 'lv'
              ? 'Tagad vari pieteikties katalogiem bez maksas.'
              : 'You can now apply to catalogs for free.'}
          </p>
        </div>
      ) : status?.verification_pending ? (
        <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-300 text-amber-800">
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p className="font-medium">
            {language === 'lv' ? 'Pieprasījums izskatīšanā' : 'Request under review'}
          </p>
          <p className="text-sm mt-1">
            {language === 'lv'
              ? 'Mūsu komanda apskatīs Tavu profilu un dokumentus tuvāko dienu laikā. Saņemsi e-pastu, kad būsim pieņēmuši lēmumu.'
              : 'Our team will review your profile and documents within a few days. You will receive an email once a decision is made.'}
          </p>
        </div>
      ) : (
        <>
          {autoVerify ? (
            <div className="p-4 rounded-xl bg-green-50 border-2 border-green-300 text-green-800 max-w-md mx-auto text-left flex items-start gap-3" data-testid="onb-registry-match">
              <ShieldCheck className="w-6 h-6 flex-shrink-0 mt-0.5 text-green-600" />
              <p className="text-sm">
                {language === 'lv'
                  ? `Reģistrācijas numurs sakrīt ar Uzņēmumu reģistru (${registryCheck.registry_name}). Veikals tiks verificēts UZREIZ.`
                  : `Registration number matches the Company Register (${registryCheck.registry_name}). Your shop will be verified INSTANTLY.`}
              </p>
            </div>
          ) : duplicate ? (
            <div className="p-4 rounded-xl bg-red-50 border-2 border-red-300 text-red-800 max-w-md mx-auto text-left" data-testid="onb-registry-duplicate">
              <p className="text-sm">
                {language === 'lv'
                  ? 'Šis reģistrācijas numurs jau ir reģistrēts citā KoLV veikalā. Drošības dēļ viens uzņēmums var būt tikai vienam veikalam.'
                  : 'This registration number is already registered to another KoLV shop. For security, a company can belong to only one shop.'}
              </p>
            </div>
          ) : (
            <p className="text-[#5C5C5C] text-sm max-w-md mx-auto">
              {language === 'lv'
                ? 'Visi soļi pabeigti! Nospied pogu zemāk, lai nosūtītu pieprasījumu mūsu komandai. Verifikācija parasti aizņem 1-3 darba dienas.'
                : 'All steps complete! Click the button below to send a request to our team. Verification usually takes 1-3 business days.'}
            </p>
          )}
          <Button
            onClick={onSubmit}
            disabled={submitting || duplicate}
            className={autoVerify ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-[#D97736] hover:bg-[#C06528] text-white'}
            data-testid="onb-submit-verification"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            {autoVerify
              ? (language === 'lv' ? 'Verificēt uzreiz' : 'Verify instantly')
              : (language === 'lv' ? 'Nosūtīt pieprasījumu' : 'Submit request')}
          </Button>
        </>
      )}
    </div>
  );
}
